import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { siteConfig } from "@/lib/site-config";

/**
 * Reputation Hub review widget — preserves original embed height,
 * no clipping, no scrollbars, no forced max-height.
 *
 * The widget (a third-party iframe plus its resizing script, which also pulls
 * remote fonts) sits well below the fold, so it is deferred until it is about
 * to scroll into view. That keeps it off the initial load entirely and a
 * reserved min-height prevents layout shift when it finally mounts.
 */
const ReviewWidget = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [inView]);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary-deep text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </motion.h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
          Real reviews from real Inland Empire customers.
        </p>
      </div>

        <motion.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
          style={{ minHeight: 600 }}
        >
          {inView && (
            <iframe
              className="lc_reviews_widget"
              src={siteConfig.widgets.reviewIframeSrc}
              frameBorder={0}
              scrolling="no"
              style={{ minWidth: "100%", width: "100%", border: "none", display: "block" }}
              title="Customer reviews"
              loading="lazy"
            />
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
